/**
 * Mini-scoreboard permanent. Les echanges de position sont animes en `transform`
 * (FLIP) et non en reflow : la liste reste dans l'ordre du salon dans le DOM,
 * seul le decalage vertical est anime, donc rien ne saute.
 */
import { useLayoutEffect, useRef, type ReactElement } from 'react';
import type { PlayerView } from '@retusch/shared';
import { Avatar } from './Avatar.js';
import { BotTag } from './BotTag.js';
import { StreamTag } from './StreamTag.js';

export interface ScoreboardProps {
  readonly players: readonly PlayerView[];
  readonly selfId: string | null;
  /**
   * Battle royale : ce mode ne compte AUCUN point.
   *
   * La colonne des scores y afficherait des zeros alignes, et le tri par score
   * mettrait tout le monde a egalite. On classe donc par survie : ceux qui
   * jouent encore d'abord, les sortis ensuite, du meilleur rang au pire.
   */
  readonly elimination?: boolean;
}

const ROW_HEIGHT = 34;

export function Scoreboard({ players, selfId, elimination = false }: ScoreboardProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousRank = useRef(new Map<string, number>());

  // Le streamer ne marque aucun point : le laisser dans le tri le collerait a
  // vie en derniere place, ce qui se lirait comme une deroute alors qu'il ne
  // concourt pas. Il est donc epingle EN TETE, hors classement, et le rang
  // numerote ne compte que les joueurs.
  const ranked = [...players].sort((a, b) => {
    if (a.streamer !== b.streamer) return a.streamer ? -1 : 1;
    if (elimination) {
      // Encore en jeu d'abord : c'est la seule hierarchie du mode. Les sortis
      // suivent dans l'ordre de leur place, la meilleure en tete.
      if (a.eliminated !== b.eliminated) return a.eliminated ? 1 : -1;
      return (a.rank ?? 0) - (b.rank ?? 0) || a.id.localeCompare(b.id);
    }
    return b.score - a.score || a.id.localeCompare(b.id);
  });
  const streamers = ranked.filter((player) => player.streamer).length;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    const rows = container.querySelectorAll<HTMLElement>('[data-player-id]');
    for (const row of rows) {
      const id = row.dataset['playerId'];
      if (id === undefined) continue;

      const next = ranked.findIndex((player) => player.id === id);
      const previous = previousRank.current.get(id);
      previousRank.current.set(id, next);
      if (previous === undefined || previous === next) continue;

      // FLIP : on repart de l'ancienne place et on laisse la transition ramener
      // la ligne a zero. Uniquement du transform, donc 60 fps garanti.
      const delta = (previous - next) * ROW_HEIGHT;
      row.style.transition = 'none';
      row.style.transform = `translateY(${delta}px)`;
      requestAnimationFrame(() => {
        row.style.transition = 'transform var(--dur-screen) var(--ease)';
        row.style.transform = 'translateY(0)';
      });
    }
  }, [ranked]);

  return (
    <div className="scoreboard" ref={containerRef}>
      {ranked.map((player, index) => (
        <div
          className="scoreboard__row"
          key={player.id}
          data-player-id={player.id}
          data-self={player.id === selfId}
          data-out={player.eliminated}
          data-streamer={player.streamer}
          style={{ order: index }}
        >
          {/* En battle royale, ce n'est pas une position dans un classement
              mais la PLACE FINALE du joueur sorti. Ceux qui jouent encore n'en
              ont pas : leur afficher un rang provisoire promettrait un
              classement qui n'existe pas. */}
          <span className="scoreboard__rank mono-tabular">
            {elimination
              ? (player.rank ?? '')
              : player.streamer
                ? ''
                : index + 1 - streamers}
          </span>
          <Avatar
            playerId={player.id} avatar={player.avatar}
            colorIndex={player.colorIndex}
            size={22}
            dimmed={!player.connected || player.eliminated}
          />
          <span className="scoreboard__name">
            {player.nickname}
            {player.bot && <BotTag size={12} />}
            {player.streamer && <StreamTag size={12} />}
          </span>
          {/* Pas de zero pour le streamer : un compteur fige a zero se lit
              comme un score, alors qu'il n'en a pas. Ni pour la battle royale,
              qui n'en compte aucun. */}
          <span className="scoreboard__score mono-tabular">
            {player.streamer || elimination ? '' : player.score}
          </span>
        </div>
      ))}
    </div>
  );
}
